import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Progress } from "@/components/ui/progress";
import { format, formatDistanceToNow } from "date-fns";
import {
  MapPin, Clock, Calendar, CheckCircle, Circle, Image as ImageIcon,
  MessageCircle, Heart, Compass, Star, Target, Eye, ArrowLeft
} from "lucide-react";

import { BACKEND_URL } from "@/config/api";

const CATEGORIES = {
  travel: { icon: MapPin, color: "#E07A5F", label: "Travel" },
  adventures: { icon: Compass, color: "#81B29A", label: "Adventures" },
  experiences: { icon: Star, color: "#F2CC8F", label: "Experiences" },
  goals: { icon: Target, color: "#264653", label: "Goals" },
  romance: { icon: Heart, color: "#EF476F", label: "Romance" },
};

const PublicTripSummary = () => {
  const { shareCode } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/public/trip/${shareCode}`);
        setSummary(res.data);
        
        // Fetch photos
        const urls = {};
        for (const photo of res.data.photos || []) {
          try {
            const photoRes = await axios.get(
              `${BACKEND_URL}/api/public/photos/${shareCode}/${photo.id}`,
              { responseType: 'blob' }
            );
            urls[photo.id] = URL.createObjectURL(photoRes.data);
          } catch (e) {
            console.error(`Failed to load photo ${photo.id}`);
          }
        }
        setPhotoUrls(urls);
      } catch (err) {
        setError(err.response?.data?.detail || "Trip not found");
      } finally {
        setLoading(false);
      }
    };

    if (shareCode) {
      fetchSummary();
    }

    return () => {
      Object.values(photoUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [shareCode]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-xl font-serif">Loading trip...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
        <div className="text-6xl mb-4">🗺️</div>
        <h1 className="font-serif text-2xl text-charcoal mb-2">Trip Not Found</h1>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Go to DuoLyst
        </Link>
      </div>
    );
  }

  const category = summary?.item ? CATEGORIES[summary.item.category] || CATEGORIES.experiences : null;
  const CategoryIcon = category?.icon || Star;
  const itineraryProgress = summary?.itinerary_stats?.total > 0
    ? Math.round((summary.itinerary_stats.completed / summary.itinerary_stats.total) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div
        className="relative py-16 px-4"
        style={{ background: `linear-gradient(135deg, ${category?.color}20 0%, ${category?.color}05 100%)` }}
      >
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `${category?.color}20` }}
            >
              <CategoryIcon className="w-5 h-5" style={{ color: category?.color }} />
            </div>
            <span
              className="text-sm font-semibold px-3 py-1 rounded-full"
              style={{ background: `${category?.color}15`, color: category?.color }}
            >
              {category?.label}
            </span>
            {summary.item.completed && (
              <span className="text-sm font-semibold px-3 py-1 rounded-full bg-secondary/15 text-secondary">
                ✓ Completed
              </span>
            )}
          </div>

          <h1 className="font-serif text-4xl font-medium text-charcoal mb-3">
            {summary.item.title}
          </h1>

          {summary.item.description && (
            <p className="text-lg text-muted-foreground mb-4">{summary.item.description}</p>
          )}

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span>by {summary.item.created_by_name}</span>
            {summary.item.target_date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {format(new Date(summary.item.target_date), "MMMM d, yyyy")}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {summary.view_count} views
            </span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-3xl mx-auto px-4 -mt-8">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card rounded-xl p-4 shadow-card text-center">
            <div className="text-2xl font-serif font-medium text-charcoal">
              {summary.itinerary_stats?.total || 0}
            </div>
            <div className="text-xs text-muted-foreground">Activities</div>
          </div>
          <div className="bg-card rounded-xl p-4 shadow-card text-center">
            <div className="text-2xl font-serif font-medium text-charcoal">
              {summary.photo_count || 0}
            </div>
            <div className="text-xs text-muted-foreground">Photos</div>
          </div>
          <div className="bg-card rounded-xl p-4 shadow-card text-center">
            <div className="text-2xl font-serif font-medium text-charcoal">
              {summary.comment_count || 0}
            </div>
            <div className="text-xs text-muted-foreground">Comments</div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-12 space-y-12">
        {/* Itinerary */}
        {summary.itinerary && summary.itinerary.length > 0 && (
          <section>
            <h2 className="font-serif text-2xl font-medium text-charcoal mb-4">Itinerary</h2>
            <Progress value={itineraryProgress} className="h-2 mb-6" />
            
            <div className="space-y-3">
              {summary.itinerary.map((activity) => (
                <div
                  key={activity.id}
                  className={`flex items-start gap-3 p-4 rounded-xl bg-card border border-charcoal/5 ${
                    activity.completed ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {activity.completed ? (
                      <CheckCircle className="w-5 h-5 text-secondary" />
                    ) : (
                      <Circle className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>

                  <div className="flex-1">
                    <h4 className={`font-medium text-charcoal ${activity.completed ? "line-through" : ""}`}>
                      {activity.title}
                    </h4>

                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                      {activity.date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {format(new Date(activity.date), "MMM d")}
                        </span>
                      )}
                      {activity.time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {activity.time}
                        </span>
                      )}
                      {activity.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {activity.location}
                        </span>
                      )}
                    </div>

                    {activity.notes && (
                      <p className="text-sm text-muted-foreground mt-2">{activity.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Photos */}
        {summary.photos && summary.photos.length > 0 && (
          <section>
            <h2 className="font-serif text-2xl font-medium text-charcoal mb-4">Memories</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {summary.photos.map((photo) => (
                <div
                  key={photo.id}
                  className="aspect-square rounded-xl overflow-hidden bg-muted"
                >
                  {photoUrls[photo.id] ? (
                    <img
                      src={photoUrls[photo.id]}
                      alt={photo.caption || "Memory"}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Comments */}
        {summary.comments && summary.comments.length > 0 && (
          <section>
            <h2 className="font-serif text-2xl font-medium text-charcoal mb-4">Discussion</h2>
            <div className="space-y-3">
              {summary.comments.map((comment) => (
                <div key={comment.id || comment.created_at} className="p-4 bg-card rounded-xl border border-charcoal/5">
                  <p className="text-charcoal">{comment.text}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {comment.created_by_name} • {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Notes */}
        {summary.item.notes && (
          <section className="p-6 bg-accent/20 rounded-xl">
            <h3 className="font-medium text-charcoal mb-2">Notes</h3>
            <p className="text-muted-foreground">{summary.item.notes}</p>
          </section>
        )}
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-charcoal/5 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <img src="/logo.png" alt="DuoLyst" className="w-8 h-8 object-contain" />
          <span className="font-serif text-lg font-semibold text-charcoal">DuoLyst</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Create your own shared bucket list at{" "}
          <Link to="/" className="text-primary hover:underline">
            DuoLyst
          </Link>
        </p>
      </footer>
    </div>
  );
};

export default PublicTripSummary;
